import React from 'react';
import { DispatchPlan } from '../../types';
import { formatDuration } from '../../utils/timeSlotUtils';
import { CheckCircleIcon, UserIcon, ClockIcon, MapPinIcon } from '@heroicons/react/24/outline';

interface AiReasonDisplayProps {
  dispatchPlans: DispatchPlan[];
  onApply?: (plan: DispatchPlan) => void;
  onReject?: (plan: DispatchPlan) => void;
}

const AiReasonDisplay: React.FC<AiReasonDisplayProps> = ({ dispatchPlans, onApply, onReject }) => {
  if (!dispatchPlans || dispatchPlans.length === 0) {
    return (
      <div className='p-6 text-center text-gray-500'>
        <div className='mb-4'>
          <UserIcon className='h-12 w-12 mx-auto text-gray-300' />
        </div>
        <p>AI配車提案がありません</p>
        <p className='text-sm mt-1'>
          「配車計画を生成」ボタンを押してAIに配車計画を作成させてください
        </p>
      </div>
    );
  }

  return (
    <div className='space-y-4 p-4'>
      <div className='flex items-center mb-4'>
        <CheckCircleIcon className='h-5 w-5 text-green-500 mr-2' />
        <h3 className='text-lg font-semibold text-green-700'>AI配車提案</h3>
      </div>

      {dispatchPlans.map((plan, index) => (
        <div
          key={`${plan.landTransporterId}-${index}`}
          className='rounded-lg border border-gray-200 bg-white p-4 shadow-sm transition-shadow hover:shadow-md dark:border-gray-700 dark:bg-gray-800'
        >
          <div className='flex items-start justify-between mb-3'>
            <div className='flex items-center'>
              <UserIcon className='h-5 w-5 text-blue-500 mr-2' />
              <h4 className='font-semibold text-gray-900 dark:text-white'>
                {plan.landTransporterName}
              </h4>
            </div>
            <span className='rounded bg-blue-100 px-2.5 py-0.5 text-xs font-medium text-blue-800 dark:bg-blue-900 dark:text-blue-200'>
              {plan.caseIds.length}件の案件
            </span>
          </div>

          {plan.reason && (
            <div className='mb-3'>
              <h5 className='mb-1 text-sm font-medium text-gray-700 dark:text-gray-300'>
                選択理由
              </h5>
              <p className='rounded bg-gray-50 p-2 text-sm text-gray-600 dark:bg-gray-700 dark:text-gray-400'>
                {plan.reason}
              </p>
            </div>
          )}

          {plan.factors && plan.factors.length > 0 && (
            <div className='mb-3'>
              <h5 className='mb-2 text-sm font-medium text-gray-700 dark:text-gray-300'>
                選択要因
              </h5>
              <div className='flex flex-wrap gap-1'>
                {plan.factors.map((factor, factorIndex) => (
                  <span
                    key={factorIndex}
                    className='inline-flex items-center rounded-full bg-green-100 px-2 py-1 text-xs font-medium text-green-800 dark:bg-green-900 dark:text-green-200'
                  >
                    {factor}
                  </span>
                ))}
              </div>
            </div>
          )}

          {plan.nearestStation && (
            <div className='mb-3'>
              <h5 className='text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 flex items-center'>
                <MapPinIcon className='h-4 w-4 mr-1' />
                最寄駅情報
              </h5>
              <div className='bg-purple-50 dark:bg-gray-700 p-3 rounded space-y-1'>
                <div className='flex justify-between text-sm'>
                  <span className='text-gray-600 dark:text-gray-400'>駅名:</span>
                  <span className='font-medium text-purple-700 dark:text-purple-400'>
                    {plan.nearestStation}
                  </span>
                </div>
                {plan.nearestStationLat !== undefined && plan.nearestStationLng !== undefined && (
                  <div className='flex justify-between text-sm'>
                    <span className='text-gray-600 dark:text-gray-400'>座標:</span>
                    <span className='font-medium text-purple-700 dark:text-purple-400 text-xs'>
                      {plan.nearestStationLat.toFixed(6)}, {plan.nearestStationLng.toFixed(6)}
                    </span>
                  </div>
                )}
              </div>
            </div>
          )}

          {(plan.assignedTimeSlots || plan.workingHours) && (
            <div className='mb-3'>
              <h5 className='mb-2 flex items-center text-sm font-medium text-gray-700 dark:text-gray-300'>
                <ClockIcon className='mr-1 h-4 w-4' />
                時間情報
              </h5>
              <div className='space-y-2 rounded bg-blue-50 p-3 dark:bg-gray-700'>
                {plan.workingHours && (
                  <div className='flex justify-between text-sm'>
                    <span className='text-gray-600 dark:text-gray-400'>勤務時間:</span>
                    <span className='font-medium text-blue-700 dark:text-blue-300'>
                      {plan.workingHours.start} - {plan.workingHours.end}
                    </span>
                  </div>
                )}
                {plan.assignedTimeSlots && (
                  <>
                    <div className='flex justify-between text-sm'>
                      <span className='text-gray-600 dark:text-gray-400'>割り当て時間:</span>
                      <span className='font-medium text-blue-700 dark:text-blue-300'>
                        {plan.assignedTimeSlots.startTime} - {plan.assignedTimeSlots.endTime}
                      </span>
                    </div>
                    <div className='flex justify-between text-sm'>
                      <span className='text-gray-600 dark:text-gray-400'>稼働時間:</span>
                      <span className='font-medium text-blue-700 dark:text-blue-300'>
                        {formatDuration(plan.assignedTimeSlots.totalDuration)}
                      </span>
                    </div>
                  </>
                )}
              </div>
            </div>
          )}

          {plan.caseIds.length > 0 && (
            <div className='border-t border-gray-100 pt-3 mt-3 dark:border-gray-700'>
              <h5 className='mb-1 text-sm font-medium text-gray-700 dark:text-gray-300'>
                割り当て案件ID
              </h5>
              <div className='flex flex-wrap gap-1'>
                {plan.caseIds.map(caseId => (
                  <span
                    key={caseId}
                    className='rounded bg-gray-100 px-2 py-1 text-xs text-gray-600 dark:bg-gray-700 dark:text-gray-400'
                  >
                    {caseId}
                  </span>
                ))}
              </div>
            </div>
          )}

          {(onApply || onReject) && (
            <div className='border-t border-gray-100 flex gap-2 pt-3 mt-4 dark:border-gray-700'>
              {onApply && (
                <button
                  onClick={() => onApply(plan)}
                  className='rounded bg-green-600 px-3 py-1 text-sm font-medium text-white transition-colors hover:bg-green-700 dark:bg-green-700 dark:hover:bg-green-600'
                >
                  適用
                </button>
              )}
              {onReject && (
                <button
                  onClick={() => onReject(plan)}
                  className='rounded bg-gray-600 px-3 py-1 text-sm font-medium text-white transition-colors hover:bg-gray-700 dark:bg-gray-700 dark:hover:bg-gray-600'
                >
                  拒否
                </button>
              )}
            </div>
          )}
        </div>
      ))}

      <div className='mt-4 rounded-lg bg-blue-50 p-3 dark:bg-blue-900'>
        <p className='text-sm text-blue-700 dark:text-blue-200'>
          💡
          AIが最適化した配車計画です。手動でさらに調整したい場合は、「手動配車モード」に切り替えてください。
        </p>
      </div>
    </div>
  );
};

export default AiReasonDisplay;
